import React, { useEffect } from "react";
import { useTheme } from "../../context/useTheme";

/**
 * The signed-out shell, rebuilt to the brand design (Claude Design `Login D` / `Login Mobile`).
 *
 * It paints the whole viewport: a pale green gradient, a faint blueprint grid that fades out
 * two-thirds down, two soft green orbs, and a construction skyline anchored to the bottom edge.
 * Children sit in a centred column under the wordmark, so every signed-out page (sign in, forgot
 * / reset password, email confirmation) gets the same treatment.
 *
 * The two designs are the same composition at two widths, so this is one responsive layout rather
 * than two: mobile takes the base classes (40px grid, smaller orbs, the skyline blown up
 * and pulled left so the buildings still read on a phone), `md:` takes the desktop values.
 */

/** Gradient start / canvas dark — painted onto html+body so overscroll matches the page. */
const PAGE_BG = { light: "#f2faf4", dark: "#0f140f" } as const;

export default function AuthLayout({
    children,
}: {
    children: React.ReactNode;
}) {
    const { toggleTheme } = useTheme();

    // Paint the document background to match, so rubber-band overscroll doesn't flash white.
    useEffect(() => {
        const originalHtmlBg = document.documentElement.style.backgroundColor;
        const originalBodyBg = document.body.style.backgroundColor;

        const updateBackgroundColors = () => {
            const isDarkMode = document.documentElement.classList.contains('dark');
            const bgColor = isDarkMode ? PAGE_BG.dark : PAGE_BG.light;

            document.documentElement.style.backgroundColor = bgColor;
            document.body.style.backgroundColor = bgColor;
        };

        updateBackgroundColors();

        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.attributeName === 'class' &&
                    mutation.target === document.documentElement) {
                    updateBackgroundColors();
                }
            });
        });

        observer.observe(document.documentElement, { attributes: true });

        return () => {
            observer.disconnect();
            document.documentElement.style.backgroundColor = originalHtmlBg;
            document.body.style.backgroundColor = originalBodyBg;
        };
    }, []);

    return (
        <div
            className="relative flex min-h-dvh flex-col items-center overflow-hidden font-figtree text-ink
                       bg-[linear-gradient(175deg,#f2faf4_0%,#e2f3e7_45%,#d3ecdb_100%)]
                       dark:bg-[linear-gradient(175deg,#101a13_0%,#0d2016_45%,#0c2a17_100%)]"
        >
            {/* Blueprint grid, faded out below the midpoint so it never fights the skyline. */}
            <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 [--auth-grid:40px] md:[--auth-grid:48px] dark:opacity-50"
                style={{
                    backgroundImage:
                        'repeating-linear-gradient(0deg,transparent 0 calc(var(--auth-grid) - 1px),rgba(22,163,74,.12) calc(var(--auth-grid) - 1px) var(--auth-grid)),' +
                        'repeating-linear-gradient(90deg,transparent 0 calc(var(--auth-grid) - 1px),rgba(22,163,74,.12) calc(var(--auth-grid) - 1px) var(--auth-grid))',
                    WebkitMaskImage: 'linear-gradient(180deg,#000 0%,#000 55%,transparent 84%)',
                    maskImage: 'linear-gradient(180deg,#000 0%,#000 55%,transparent 84%)',
                }}
            />

            {/* Two soft orbs, top-right and mid-left. */}
            <div
                aria-hidden="true"
                className="pointer-events-none absolute -top-30 -right-30 size-80 rounded-full bg-[rgba(134,239,172,.28)]
                           md:-top-40 md:-right-45 md:size-130 dark:opacity-25"
            />
            <div
                aria-hidden="true"
                className="pointer-events-none absolute top-45 -left-35 size-70 rounded-full bg-[rgba(22,163,74,.10)]
                           md:top-30 md:-left-55 md:size-115 dark:opacity-40"
            />

            <div className="fixed top-5 right-5 z-50">
                <button
                    type="button"
                    title="Light / dark mode"
                    aria-label="Light / dark mode"
                    onClick={toggleTheme}
                    className="grid size-9 place-items-center rounded-full bg-surface/70 text-icon shadow-[0_2px_10px_rgba(20,83,45,.12)]
                               transition hover:bg-surface"
                >
                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
                    </svg>
                </button>
            </div>

            {/* The centred column. The tall bottom padding is what keeps the form clear of the skyline.
                On mobile the skyline is scaled to the VIEWPORT WIDTH, so a fixed reserve is only
                right at one width — 210px cleared the crane at 390px and let it cut through the
                footer line of the taller register form on a wider phone. The reserve tracks the
                image's own height instead: 150% of the viewport width at a 2.937:1 aspect is
                51.1vw, plus a little slack.

                On a SHORT desktop viewport (a laptop, a smaller monitor) the fixed 240px reserve plus
                the full-size form ran past the bottom of the screen and scrolled the skyline off.
                There the column starts at the top instead of centring, the reserve becomes 14vw —
                the same share of the skyline (34vw tall on desktop) that 240px leaves clear at
                1920 wide — and the logo, fields and button step down (see `short:` / `shorter:`
                in index.css and authControls.ts). */}
            <div className="relative z-1 flex w-full max-w-xl flex-1 flex-col items-center justify-center
                            px-6.5 pt-10 pb-[max(210px,calc(51.1vw+14px))] md:px-6 md:pt-12 md:pb-[240px]
                            short:justify-start short:pt-7 short:pb-[14vw] shorter:pt-4 shorter:pb-[12vw]">
                <img
                    src="/images/logo/DA-Connect_logo_title_transparent_white_logo.svg"
                    alt="DAconnect"
                    className="mb-[22px] block h-auto w-[170px] md:mb-7 md:w-[200px] short:mb-4 short:w-[150px] shorter:mb-3 shorter:w-[130px] dark:hidden"
                />
                <img
                    src="/images/logo/DA-Connect_logo_title_transparent_dark_logo.svg"
                    alt="DAconnect"
                    className="mb-[22px] hidden h-auto w-[170px] md:mb-7 md:w-[200px] short:mb-4 short:w-[150px] shorter:mb-3 shorter:w-[130px] dark:block"
                />

                {children}
            </div>

            {/* Skyline: full-bleed on desktop, blown up and pulled left on a phone so the buildings
                read. 150%, not more — the crane is the tallest thing in the frame and a bigger
                blow-up walks it into the middle of the column, under the centred text. */}
            <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 bottom-0 z-0">
                <img
                    src="/images/auth-skyline.webp"
                    alt=""
                    className="-ml-[25%] block h-auto w-[150%] max-w-none md:ml-0 md:w-full dark:opacity-35"
                />
            </div>
        </div>
    );
}
